import React, { useEffect, useRef, useState } from 'react';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ChevronLeftRounded as CloseDrawerIcon,
  ChevronRightRounded as OpenDrawerIcon,
  SearchRounded as FindIcon,
  VisibilityOffOutlined as HideAllIcon,
  VisibilityOutlined as ShowAllIcon,
} from '@mui/icons-material';
import CompactSearchSortControl, { CompactSortOption } from '../common/CompactSearchSortControl';
import ExecutionTripCard from './ExecutionTripCard';
import type { ExecutionPhase, ExecutionTripSort, ExecutionTripViewModel } from './executionTypes';

interface ExecutionTripDrawerProps {
  trips: ExecutionTripViewModel[];
  activeTripId: string | null;
  highlightedTripId?: string | null;
  visibleTripIds: string[];
  query: string;
  sort: ExecutionTripSort;
  sortDirection: 'asc' | 'desc';
  phases: ExecutionPhase[];
  loading: boolean;
  onQueryChange: (query: string) => void;
  onFindExact: () => void;
  onSortChange: (sort: ExecutionTripSort) => void;
  onSortDirectionChange: (direction: 'asc' | 'desc') => void;
  onTogglePhase: (phase: ExecutionPhase) => void;
  onOpenTrip: (tripId: string) => void;
  onToggleVisibility: (tripId: string) => void;
  onFocusTrip: (tripId: string) => void;
  onOpenTimeline: (tripId: string) => void;
  onShowAll: () => void;
  onHideAll: () => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  width: number;
  onWidthChange: (width: number) => void;
  bottomSheet?: boolean;
}

const SORT_OPTIONS: CompactSortOption<ExecutionTripSort>[] = [
  { value: 'attention', label: 'Attention first' },
  { value: 'tripId', label: 'Trip ID' },
  { value: 'progress', label: 'Progress' },
  { value: 'delay', label: 'Delay' },
  { value: 'distance', label: 'Actual distance' },
];

const PHASES: ExecutionPhase[] = ['ACTIVE', 'DELAYED', 'PENDING', 'COMPLETED'];

const ExecutionTripDrawer: React.FC<ExecutionTripDrawerProps> = (props) => {
  const theme = useTheme();
  const { onWidthChange } = props;
  const resizeRef = useRef<{ startX: number; width: number } | null>(null);
  const [resizing, setResizing] = useState(false);

  useEffect(() => {
    const handleMove = (event: PointerEvent) => {
      if (!resizeRef.current) return;
      const nextWidth = Math.min(620, Math.max(340, resizeRef.current.width + event.clientX - resizeRef.current.startX));
      onWidthChange(nextWidth);
    };
    const handleUp = () => {
      resizeRef.current = null;
      setResizing(false);
    };
    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
    return () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
    };
  }, [onWidthChange]);

  if (!props.open) {
    return (
      <Tooltip title="Open Trip drawer">
        <IconButton
          aria-label="Open Trip drawer"
          onClick={() => props.onOpenChange(true)}
          sx={{
            position: 'absolute', left: 12, top: props.bottomSheet ? 'auto' : 12, bottom: props.bottomSheet ? 12 : 'auto', zIndex: 25,
            width: 40, height: 40, bgcolor: alpha(theme.palette.background.paper, 0.94),
            border: '1px solid', borderColor: 'divider', boxShadow: 3,
          }}
        >
          <OpenDrawerIcon />
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <Paper
      square
      elevation={4}
      data-testid="execution-trip-drawer"
      data-layout-mode={props.bottomSheet ? 'bottom-sheet' : 'overlay'}
      data-drawer-width={props.bottomSheet ? 'full' : String(props.width)}
      sx={{
        position: 'absolute', inset: props.bottomSheet ? 'auto 0 0 0' : '0 auto 0 0', zIndex: 20,
        width: props.bottomSheet ? '100%' : props.width,
        height: props.bottomSheet ? '62%' : 'auto',
        maxHeight: props.bottomSheet ? 620 : 'none',
        display: 'flex', flexDirection: 'column',
        borderRadius: props.bottomSheet ? '16px 16px 0 0' : 0,
        bgcolor: alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.96 : 0.94),
        backdropFilter: 'blur(12px)', borderRight: '1px solid', borderColor: 'divider',
      }}
    >
      <Box sx={{ px: 1.35, pt: 1.2, pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
        <CompactSearchSortControl
          query={props.query}
          onQueryChange={props.onQueryChange}
          onQuerySubmit={props.onFindExact}
          placeholder="Trip, vehicle, driver or point"
          sortKey={props.sort}
          sortDirection={props.sortDirection}
          sortOptions={SORT_OPTIONS}
          onSortKeyChange={props.onSortChange}
          onSortDirectionChange={props.onSortDirectionChange}
          leadingAction={(
            <Stack direction="row" spacing={0.25} alignItems="center">
              <Tooltip title="Collapse Trip drawer">
                <IconButton size="small" aria-label="Collapse Trip drawer" onClick={() => props.onOpenChange(false)}><CloseDrawerIcon sx={{ fontSize: 18 }} /></IconButton>
              </Tooltip>
              <Tooltip title="Find exact Trip ID across dates">
                <span>
                  <IconButton size="small" aria-label="Find exact Trip ID" onClick={props.onFindExact} disabled={!props.query.trim()}>
                    <FindIcon sx={{ fontSize: 17 }} />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          )}
        />
        <Stack direction="row" spacing={0.45} alignItems="center" useFlexGap flexWrap="wrap" sx={{ mt: 0.8 }}>
          {PHASES.map((phase) => (
            <Chip
              key={phase}
              size="small"
              clickable
              label={phase}
              color={props.phases.includes(phase) ? 'primary' : 'default'}
              variant={props.phases.includes(phase) ? 'filled' : 'outlined'}
              onClick={() => props.onTogglePhase(phase)}
              sx={{ height: 22, fontSize: 9.5 }}
            />
          ))}
          <Box sx={{ flex: 1 }} />
          <Tooltip title="Show all Trips"><IconButton size="small" aria-label="Show all Trips" onClick={props.onShowAll}><ShowAllIcon sx={{ fontSize: 17 }} /></IconButton></Tooltip>
          <Tooltip title="Hide all Trips"><IconButton size="small" aria-label="Hide all Trips" onClick={props.onHideAll}><HideAllIcon sx={{ fontSize: 17 }} /></IconButton></Tooltip>
        </Stack>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 1.15 }}>
        {props.loading ? (
          <Box sx={{ height: 180, display: 'grid', placeItems: 'center' }}><CircularProgress size={26} /></Box>
        ) : props.trips.length ? props.trips.map((trip) => (
          <ExecutionTripCard
            key={trip.id}
            trip={trip}
            active={props.activeTripId === trip.id}
            highlighted={props.highlightedTripId === trip.id}
            visible={props.visibleTripIds.includes(trip.id)}
            onOpen={() => props.onOpenTrip(trip.id)}
            onToggleVisibility={() => props.onToggleVisibility(trip.id)}
            onFocus={() => props.onFocusTrip(trip.id)}
            onOpenTimeline={() => props.onOpenTimeline(trip.id)}
          />
        )) : (
          <Stack alignItems="center" spacing={1} sx={{ py: 8, px: 2, textAlign: 'center' }}>
            <Typography variant="subtitle2">No Trips match this view</Typography>
            <Typography variant="caption" color="text.secondary">Clear search or status filters, or refresh the selected day.</Typography>
            <Button size="small" variant="outlined" onClick={() => props.onQueryChange('')}>Clear search</Button>
          </Stack>
        )}
      </Box>
      <Box
        data-testid="execution-trip-drawer-resize"
        onPointerDown={(event) => {
          event.preventDefault();
          resizeRef.current = { startX: event.clientX, width: props.width };
          setResizing(true);
        }}
        sx={{
          display: props.bottomSheet ? 'none' : 'block',
          position: 'absolute', inset: '0 -5px 0 auto', width: 10, cursor: 'ew-resize', zIndex: 2,
          bgcolor: resizing ? alpha(theme.palette.primary.main, 0.15) : 'transparent',
          '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.09) },
        }}
      />
    </Paper>
  );
};

export default ExecutionTripDrawer;
